import type { DevToolboxScenarioId } from "@/lib/devToolbox/types";
import { DEV_TOOLBOX_MOCK_ITEM_ID } from "@/lib/devToolbox/itemDetailMocks";

export const DEV_TOOLBOX_PURCHASE_MOCK_ID = "dev-toolbox-purchase-mock";

export type DevPurchaseMockStatus = "pending" | "completed" | "cancelled";

export type DevPurchaseMockPayload = {
  transaction: {
    id: string;
    status: DevPurchaseMockStatus;
    amount: number;
    item_id: string;
    item_title: string;
    seller_id: string;
    buyer_id: string;
    seller_approved: boolean;
    buyer_approved: boolean;
    created_at: string;
    updated_at: string;
  };
  item: {
    id: string;
    title: string;
    description: string;
    price: number;
    category: string;
    location: string;
    status: string;
    images: Array<{ url: string }>;
    seller_id: string;
  };
  seller: {
    id: string;
    name: string;
    location: string;
    phone: string;
  };
  buyer: {
    name: string;
  };
};

const BASE_MOCK_PURCHASE: DevPurchaseMockPayload = {
  transaction: {
    id: DEV_TOOLBOX_PURCHASE_MOCK_ID,
    status: "pending",
    amount: 180_000,
    item_id: DEV_TOOLBOX_MOCK_ITEM_ID,
    item_title: "Dev Mock Sofa Set",
    seller_id: "dev-mock-seller-id",
    buyer_id: "dev-mock-buyer-id",
    seller_approved: false,
    buyer_approved: false,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  item: {
    id: DEV_TOOLBOX_MOCK_ITEM_ID,
    title: "Dev Mock Sofa Set",
    description:
      "Mock purchase detail for dev toolbox — not a real transaction.",
    price: 180_000,
    category: "furniture",
    location: "Lagos, Ikeja",
    status: "approved",
    images: [
      {
        url: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&w=400&q=80",
      },
    ],
    seller_id: "dev-mock-seller-id",
  },
  seller: {
    id: "dev-mock-seller-id",
    name: "Dev Mock Seller",
    location: "Lagos, Ikeja",
    phone: "2348012345678",
  },
  buyer: {
    name: "Dev Mock Buyer",
  },
};

const PURCHASE_STATUS_BY_SCENARIO: Partial<
  Record<DevToolboxScenarioId, DevPurchaseMockStatus>
> = {
  "buyer-transaction.purchase-pending": "pending",
  "buyer-transaction.purchase-completed": "completed",
  "buyer-transaction.purchase-cancelled": "cancelled",
};

export function getDevPurchaseMockStatus(
  scenarioId: DevToolboxScenarioId | null,
): DevPurchaseMockStatus | null {
  if (!scenarioId) return null;
  return PURCHASE_STATUS_BY_SCENARIO[scenarioId] ?? null;
}

export function buildDevPurchaseMock(
  status: DevPurchaseMockStatus,
): DevPurchaseMockPayload {
  const buyerApproved = status === "completed";
  const sellerApproved = status === "completed";

  return {
    ...BASE_MOCK_PURCHASE,
    transaction: {
      ...BASE_MOCK_PURCHASE.transaction,
      status,
      buyer_approved: buyerApproved,
      seller_approved: sellerApproved,
    },
    item: { ...BASE_MOCK_PURCHASE.item },
    seller: { ...BASE_MOCK_PURCHASE.seller },
    buyer: { ...BASE_MOCK_PURCHASE.buyer },
  };
}

/** Shape expected by `my-purchases/[id]/page.tsx` after its normal mapper. */
export function buildDevPurchaseDetailsView(status: DevPurchaseMockStatus) {
  const mock = buildDevPurchaseMock(status);
  return {
    transaction: {
      ...mock.transaction,
      itemId: mock.transaction.item_id,
      sellerId: mock.transaction.seller_id,
      buyerId: mock.transaction.buyer_id,
      sellerApproved: mock.transaction.seller_approved,
      buyerApproved: mock.transaction.buyer_approved,
      pickupDate: undefined,
      createdAt: new Date(mock.transaction.created_at),
      updatedAt: new Date(mock.transaction.updated_at),
    },
    item: mock.item,
    seller: mock.seller,
    buyer: mock.buyer,
  };
}

export const DEV_PAYMENT_SUCCESS_MOCK = {
  item: {
    id: DEV_TOOLBOX_MOCK_ITEM_ID,
    title: "Dev Mock Sofa Set",
    price: 180_000,
    images: [
      {
        url: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&w=400&q=80",
      },
    ],
  },
  transaction: {
    id: "dev-toolbox-tx-mock",
    status: "completed" as const,
    amount: 180_000,
    item_id: DEV_TOOLBOX_MOCK_ITEM_ID,
    buyer_id: "dev-mock-buyer-id",
    seller_id: "dev-mock-seller-id",
    buyer_approved: true,
    seller_approved: true,
    item_title: "Dev Mock Sofa Set",
    payment_method: "bank_transfer",
    payment_ref: "DEV-MOCK-PAYMENT-REF",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  reference: "DEV-MOCK-PAYMENT-REF",
};
